import { type RedirectQsItem } from "@client/actions";
import { makeModuleApiInfoSelector } from "@client/content/selectors";
import { toCamelCase } from "@client/lib/misc";
import { TargetSelector, getSettingValueSelector, makeModuleEntitySelector } from "@client/selectors";
import { type AppState, type BaseAppState, type EntityRef, type GenericMap, Status } from "@client/types";
import { type Dispatch } from "redux";
import { initAction, redirect } from "../actions";
import { makeFieldsSelector } from "./Form/selectors";
import api from "./api";
import { processEnroll } from "./lib";
import { localStateSelector } from "./selectors";
import { type OptInFormV2 } from "./types";

export enum Actions {
  InitBegin = "OPT_IN_FORM_V2_INIT_BEGIN",
  InitFailure = "OPT_IN_FORM_V2_INIT_FAILURE",
  InitSuccess = "OPT_IN_FORM_V2_INIT_SUCCESS",

  SetModalVisible = "OPT_IN_FORM_V2_SET_MODAL_VISIBLE"
}

export type SendOtpBeginActionData = EntityRef & {
  otpStepId: string;
  fields: GenericMap<any>;
};

export interface SendOtpSuccessActionData {
  expiryTime: Date;
}

export type InitBeginActionData = EntityRef & {};
export interface InitSuccessActionData {
  consentFieldIds: string[];
  fields: OptInFormV2.FieldInfo[];
  funnelPathId: string | null;
  isAllowed: boolean;
  countryCode: string | null;
}
export const _init = initAction<BaseAppState, InitBeginActionData, OptInFormV2.Api.InitResponse, InitSuccessActionData>(
  {
    types: {
      begin: Actions.InitBegin,
      failure: Actions.InitFailure,
      success: Actions.InitSuccess
    },
    action: (state: BaseAppState, dispatch: any, args: InitBeginActionData) => {
      const apiBaseArgs = makeModuleApiInfoSelector()(state, args);

      return api.init(apiBaseArgs, {});
    }
  }
);

export type InitArgs = EntityRef & {};
export const init = (args: InitArgs) => (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
  const state = getState();

  const localState = localStateSelector(state, args);
  if (localState.status === Status.None) {
    dispatch(_init(args));
  }
};

export type SubmitBeginActionData = EntityRef & {
  formData: OptInFormV2.SubmitData;
  otpStepId?: string;
  otp?: string;
};
export type SubmitSuccessActionData = OptInFormV2.Api.SubmitResponse;

export type SubmittedArgs = {
  customFieldValues: GenericMap<any>;
  enroll: OptInFormV2.Api.ProgramEnroll | null;
  fieldValues: GenericMap<any>;
  userId: number;
};
export const submitted =
  (ref: EntityRef, args: SubmittedArgs) => (dispatch: Dispatch<any>, getState: () => AppState) => {
    const state = getState();
    if (processEnroll(state, args.enroll)) return;

    const confirmation = getSettingValueSelector(TargetSelector.Entity, "confirmation")(state, ref);
    const entity = makeModuleEntitySelector()(state, ref);

    if (confirmation === "redirect") {
      const fields = makeFieldsSelector()(state, ref);

      const values = {
        ...args.fieldValues,
        ...args.customFieldValues
      };
      const qs = {
        ...Object.keys(values).reduce<GenericMap<RedirectQsItem>>((res, x) => {
          const field = fields.find((y) => y.id.toLowerCase() === x.toLowerCase());
          if (!field || !field.formFieldKey) return res; // protection

          return {
            ...res,
            [field.formFieldId]: {
              key: field.isCustom ? field.formFieldId.toLowerCase() : toCamelCase(field.formFieldKey!),
              value: values[x]
            }
          };
        }, {}),
        uid: args.userId
      };

      dispatch(
        redirect({
          fallbackUrl: entity.moduleType === "Register" ? "/" : undefined,
          id: ref.id,
          isOptIn: true,
          reload: entity.moduleType === "Register",
          source: ref.source,
          target: TargetSelector.Entity,
          qs
        })
      );
    }
  };

export type SetModalVisibleActionData = EntityRef & {
  isVisible: boolean;
};
export const setModalVisible =
  (ref: EntityRef, isVisible: boolean) => (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
    dispatch({
      type: Actions.SetModalVisible,
      data: {
        ...ref,
        isVisible
      }
    });

    // TEMP SOLUTION
    const state = getState();
    if (isVisible && state.isEditMode) {
      dispatch({
        type: "MODAL_STICK_CHANGED",
        data: {
          stickSide: state["modals"]?.stickSide ?? "right",
          width: 300
        }
      });
    }
  };
